import { useMemo } from "react";
import { useDigitalTwinData } from "./queries/useDigitalTwinData";
import { useRouteState } from "./useRouteState";

/**
 * Minimal report data hook for the GenerateReportPopup.
 * Only surfaces the pieces the UI needs: plan ids plus twin/patient ids.
 */
export const useReportData = () => {
  const { params } = useRouteState();
  const patientId = params?.patientId || null;
  const twinId = params?.twinId || null;

  const digitalTwinIds = useMemo(() => {
    return twinId ? [twinId] : [];
  }, [twinId]);

  const { digitalTwins } = useDigitalTwinData(digitalTwinIds);
  const twin = digitalTwins?.[0] || null;

  const planIds = useMemo(() => {
    if (!Array.isArray(twin?.plans)) {
      return [];
    }
    return twin.plans.filter(Boolean);
  }, [twin?.plans]);

  const plansList = useMemo(() => {
    // Keep shape compatible with GenerateReportPopup (expects objects with _id)
    return planIds.map(id => ({ _id: id }));
  }, [planIds]);

  const selectedPlanId = useMemo(() => {
    if (twin?.selected_plan) {
      return twin.selected_plan;
    }
    if (planIds.length > 0) {
      return planIds[0];
    }
    return null;
  }, [twin?.selected_plan, planIds]);

  const digitalTwinId = useMemo(() => {
    return twin?._id || twinId || null;
  }, [twin?._id, twinId]);

  return {
    allPlans: plansList,
    selectedPlanId,
    digitalTwinId,
    patientId,
  };
};
